import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from "@ngrx/store";
import {
  BuildTeamApiActions,
  BuildTeamPageActions,
  TeamMemberNameFilterActions,
  TeamSkillsMatrixFilterActions,
} from "@skills-app/build-team/actions";
import * as BuildTeamActions from "@skills-app/build-team/actions/build-team-page.actions";
import { SearchBySkillOrConsultantPageActions } from "@skills-app/consultant-skill/actions";
import {
  getOrgs,
  getSortedFilteredConsultantsByMatchingSkills,
} from "@skills-app/consultant-skill/reducers/consultant-search.reducer";
import { NavmenuActions } from "@skills-app/core/actions";
import { ISkillNode } from "@skills-app/models/skillNode";
import ISkillNodeWithRating from "@skills-app/models/skillNodeWithRating";
import SkillRating, {
  getSkillChartOrder,
  getSkillRatingIndex,
} from "@skills-app/models/skillRating";
import { ITeam } from "@skills-app/models/team";
import { ITeamSkillNode } from "@skills-app/models/teamSkillNode";
import IUserSkill from "@skills-app/models/userSkills";
import { getSelectedSkillProficiencyRange } from "@skills-app/skill/reducers/skill.reducer";

// ---------------------------------------------------------
// Helpers: normalise identity across sources (AzureId/AzureID/azureId)
// Dedup and remove using AzureId (preferred) or Email (fallback).
// ---------------------------------------------------------
const getAzureId = (u: any): string => {
  const v = u?.AzureId || u?.AzureID || u?.azureId || "";
  return typeof v === "string" ? v : String(v ?? "");
};

const getUserKey = (u: any): string => {
  const id = getAzureId(u).trim();
  if (id) return id;
  const email = (u?.Email ?? "").toString().trim();
  return email;
};

const asArray = <T>(v: any): T[] => (Array.isArray(v) ? (v as T[]) : []);

const pickMappedSkills = (u: any): ISkillNodeWithRating[] => {
  const mapped =
    u?.MappedSkills ?? u?.mappedSkills ?? u?.mappedskills ?? u?.Mappedskills;

  if (Array.isArray(mapped) && mapped.length > 0) {
    return mapped as ISkillNodeWithRating[];
  }

  const skills =
    u?.Skills ?? u?.skills ?? u?.UserSkills ?? u?.userSkills ?? u?.userskills;

  if (Array.isArray(skills) && skills.length > 0) {
    return skills as ISkillNodeWithRating[];
  }

  return [];
};

const normaliseUserSkill = (u: any): any => {
  if (u == null) return u;

  let v = u;

  // Normalise AzureId casing
  const id = getAzureId(v).trim();
  if (id && !v?.AzureId) {
    v = { ...v, AzureId: id };
  }

  // Normalise skills shape for matrix + dialogs:
  // If MappedSkills missing/empty but Skills exists, map Skills -> MappedSkills.
  const mappedSkills = pickMappedSkills(v);

  const existingMapped = asArray<ISkillNodeWithRating>(v?.MappedSkills);
  if (existingMapped.length === 0 && mappedSkills.length > 0) {
    v = { ...v, MappedSkills: mappedSkills };
  }

  // Ensure MappedSkills is always at least an empty array
  if (!Array.isArray(v?.MappedSkills)) {
    v = { ...v, MappedSkills: [] as ISkillNodeWithRating[] };
  }

  return v;
};

export interface ITeamState {
  teamMembers: IUserSkill[];
  existingTeam: ITeam | null;
  teamMemberNameFilterString: string;
  teamSummarySkillFilterSkills: ISkillNode[];
  skillsToShow: number;
  isLoading: ITeamLoading;
  myTeams: ITeam[];
  teamNotFound: boolean;
}

export interface ITeamLoading {
  upsertTeam: boolean;
  generateCSV: boolean;
  deleteTeam: boolean;
}

const emptyTeam: ITeam = {
  Id: 0,
  Name: null as unknown as ITeam["Name"],
  Slug: null as unknown as ITeam["Slug"],
  TeamMemberGuids: null as unknown as ITeam["TeamMemberGuids"],
  IsOwner: null as unknown as ITeam["IsOwner"],
};

const initialState: ITeamState = {
  teamMembers: [],
  existingTeam: emptyTeam,
  teamMemberNameFilterString: "",
  teamSummarySkillFilterSkills: [],
  skillsToShow: 0,
  isLoading: {
    upsertTeam: false,
    generateCSV: false,
    deleteTeam: false,
  },
  myTeams: [],
  teamNotFound: false,
};

export const reducer = createReducer(
  initialState,

  on(BuildTeamApiActions.populateTeamMembers, (state, action) => ({
    ...state,
    teamMembers: (action.teamMembers ?? []).map(normaliseUserSkill),
  })),

  on(BuildTeamApiActions.populateExistingTeam, (state, action) => ({
    ...state,
    existingTeam: action.team,
    teamNotFound: false,
    myTeams: updateTeams(action.team, [...state.myTeams]),
  })),

  on(
    BuildTeamPageActions.addToTeam,
    SearchBySkillOrConsultantPageActions.addToTeam,
    (state, action) => {
      const existingKeys = new Set(
        (state.teamMembers ?? []).map(getUserKey).filter(Boolean),
      );

      const toAddRaw = (action.userSkills ?? []).map(normaliseUserSkill);
      const toAdd = toAddRaw.filter((u) => {
        const key = getUserKey(u);
        return !!key && !existingKeys.has(key);
      });

      return {
        ...state,
        teamMembers: [...(state.teamMembers ?? []), ...toAdd],
      };
    },
  ),

  on(
    BuildTeamPageActions.removeFromTeam,
    SearchBySkillOrConsultantPageActions.removeFromTeam,
    (state, action) => {
      const removeKey = getUserKey(action.userSkill);
      if (!removeKey) return state;

      return {
        ...state,
        teamMembers: (state.teamMembers ?? []).filter(
          (u) => getUserKey(u) !== removeKey,
        ),
      };
    },
  ),

  on(
    TeamMemberNameFilterActions.loadComponent,
    TeamMemberNameFilterActions.unloadComponent,
    () => ({
      ...initialState,
      existingTeam: emptyTeam,
    }),
  ),

  on(TeamMemberNameFilterActions.filterTeamMembersByName, (state, action) => ({
    ...state,
    teamMemberNameFilterString: action.filterString,
  })),

  on(
    TeamSkillsMatrixFilterActions.loadComponent,
    TeamSkillsMatrixFilterActions.unloadComponent,
    (state) => ({
      ...state,
      teamSummarySkillFilterSkills: [],
    }),
  ),

  on(BuildTeamActions.addSkillToTeamMatrixFilter, (state, action) => ({
    ...state,
    teamSummarySkillFilterSkills: [
      ...state.teamSummarySkillFilterSkills,
      action.skill,
    ],
  })),

  on(BuildTeamActions.addAllSkillsToTeamMatrixFilter, (state, action) => ({
    ...state,
    teamSummarySkillFilterSkills: action.skills,
  })),

  on(BuildTeamActions.removeSkillFromTeamMatrixFilter, (state, action) => ({
    ...state,
    teamSummarySkillFilterSkills: state.teamSummarySkillFilterSkills.filter(
      (skill) => skill.Id !== action.skill.Id,
    ),
  })),

  on(BuildTeamActions.removeAllSkillsFromTeamMatrixFilter, (state) => ({
    ...state,
    teamSummarySkillFilterSkills: [],
  })),

  on(BuildTeamPageActions.upsertTeam, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, upsertTeam: true },
  })),

  on(
    BuildTeamApiActions.teamUpserted,
    BuildTeamApiActions.upsertTeamError,
    (state) => ({
      ...state,
      isLoading: { ...state.isLoading, upsertTeam: false },
    }),
  ),

  on(NavmenuActions.endNavigation, (state) => ({
    ...state,
    teamMembers: [],
    existingTeam: emptyTeam,
  })),

  on(BuildTeamApiActions.loadAllTeamsSuccess, (state, action) => ({
    ...state,
    myTeams: action.teams,
  })),

  on(
    TeamSkillsMatrixFilterActions.setAmountOfSkillsToShow,
    (state, action) => ({
      ...state,
      skillsToShow: action.amount,
    }),
  ),

  on(BuildTeamApiActions.populateExistingTeamError, (state) => ({
    ...state,
    teamNotFound: true,
  })),

  on(BuildTeamPageActions.generateCSVForTeam, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, generateCSV: true },
  })),

  on(BuildTeamApiActions.generateCSVForTeamSuccess, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, generateCSV: false },
  })),

  on(BuildTeamApiActions.generateCSVForTeamError, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, generateCSV: false },
  })),

  on(BuildTeamPageActions.deleteTeam, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, deleteTeam: true },
  })),

  on(BuildTeamApiActions.teamDeleted, (state) => ({
    ...state,
    existingTeam: null,
    isLoading: { ...state.isLoading, deleteTeam: false },
  })),
);

export const getTeam = createFeatureSelector<ITeamState>("teams");

export const getAllTeams = createSelector(
  getTeam,
  (teamState) => teamState.myTeams,
);

export const getTeamLoading = createSelector(
  getTeam,
  (state) => state.isLoading,
);

export const getTeamNotFound = createSelector(
  getTeam,
  (state) => state.teamNotFound,
);

/**
 * KEY FIX:
 * Normalise each user here so all downstream selectors (especially matrix skills)
 * always see canonical IUserSkill shape (AzureId + MappedSkills).
 */
export const getTeamMembers = createSelector(
  getTeam,
  getOrgs,
  (teamState, orgs) =>
    (teamState.teamMembers ?? []).map((raw) => {
      const u = normaliseUserSkill(raw);
      return {
        ...u,
        Org: (orgs ?? []).find((s) => s.Id === u.OrgId),
      } as IUserSkill;
    }),
);

export const getExistingTeam = createSelector(
  getTeam,
  (teamState) => teamState.existingTeam,
);

export const getCurrentTeamSkills = createSelector(
  getTeamMembers,
  (teamMembers) => {
    const allMappedSkills = (teamMembers ?? []).map(
      (u) => u.MappedSkills ?? [],
    );
    return allMappedSkills.reduce(
      (acc, arr) => acc.concat(arr),
      [] as ISkillNodeWithRating[],
    );
  },
);

/**
 * People list for Build Team:
 * Use the relevance-sorted list from consultant-search reducer (Option A),
 * then remove anyone already in the team.
 */
export const getFilteredConsultantsNotInTeam = createSelector(
  getTeamMembers,
  getSortedFilteredConsultantsByMatchingSkills,
  (teamMembers, sortedConsultants) => {
    if (!sortedConsultants) return sortedConsultants;

    const teamKeys = new Set(
      (teamMembers ?? []).map(getUserKey).filter(Boolean),
    );
    return sortedConsultants.filter(
      (consultant: any) => !teamKeys.has(getUserKey(consultant)),
    );
  },
);

export const getDistinctTeamSkillNames = createSelector(
  getCurrentTeamSkills,
  (teamSkills) =>
    Array.from(new Set((teamSkills ?? []).map((skill) => skill.Name))).sort(),
);

export const getNumberOfDistinctSkillsWithinProficiencyRange = createSelector(
  getCurrentTeamSkills,
  getSelectedSkillProficiencyRange,
  (teamSkills, skillProficiencyRange) => {
    const minIndex = getSkillRatingIndex(skillProficiencyRange.min);
    const maxRaw = skillProficiencyRange.max;
    const maxIndex =
      maxRaw > 5 && maxRaw !== 99 ? 5 : getSkillRatingIndex(maxRaw);

    return Array.from(
      new Set(
        (teamSkills ?? [])
          .filter(
            (skill) =>
              getSkillRatingIndex(skill.SkillRating) >= minIndex &&
              getSkillRatingIndex(skill.SkillRating) <= maxIndex,
          )
          .map((s) => s.Name),
      ),
    ).length;
  },
);

export const getTeamMemberNameFilterString = createSelector(
  getTeam,
  (teamState) => teamState.teamMemberNameFilterString,
);

export const getTeamSummarySkillFilterSkills = createSelector(
  getTeam,
  (teamState) => teamState.teamSummarySkillFilterSkills,
);

export const getTeamMatrixSkillsToDisplay = createSelector(
  getDistinctTeamSkillNames,
  getTeamSummarySkillFilterSkills,
  (distinctSkillNames, filterSkills) =>
    filterSkills != null && filterSkills.length > 0
      ? filterSkills.map((skill) => skill.Name)
      : distinctSkillNames,
);

export const getSkillsToShow = createSelector(
  getTeam,
  (teamState) => teamState.skillsToShow,
);

export const getFilteredTeamMembers = createSelector(
  getTeamMembers,
  getTeamMemberNameFilterString,
  (teamMembers, teamMemberNameFilterString) =>
    teamMemberNameFilterString != null && teamMemberNameFilterString !== ""
      ? teamMembers.filter(
          (teamMember) =>
            teamMember.DisplayName.toLowerCase().indexOf(
              teamMemberNameFilterString.toLowerCase(),
            ) !== -1,
        )
      : teamMembers,
);

export const getTeamSkillMatrixData = createSelector(
  getCurrentTeamSkills,
  getTeamMatrixSkillsToDisplay,
  (teamSkills, skillsToDisplay) =>
    skillsToDisplay.map((skillName) => {
      const countOfProficienciesForSkill: (string | number)[] = [skillName];

      const teamCurrentSkill = (teamSkills ?? []).filter(
        (skill) => skill.Name === skillName,
      );

      const skillRatingOrder = getSkillChartOrder();
      skillRatingOrder.forEach((skillRating) => {
        const count = teamCurrentSkill.filter(
          (skill) => skill.SkillRating === skillRating,
        ).length;
        countOfProficienciesForSkill.push(count);
        countOfProficienciesForSkill.push(count > 0 ? count : "");
      });

      return countOfProficienciesForSkill;
    }),
);

export const getTeamSkillMatrixHViewWindowMax = createSelector(
  getCurrentTeamSkills,
  getTeamMatrixSkillsToDisplay,
  (teamSkills, skillsToDisplay) => {
    const filteredTeamSkills = (teamSkills ?? []).filter((skill) =>
      skillsToDisplay.includes(skill.Name),
    );

    const counts = filteredTeamSkills.reduce(
      (hashMap, skill) => {
        hashMap[skill.Name] = (hashMap[skill.Name] ?? 0) + 1;
        return hashMap;
      },
      {} as Record<string, number>,
    );

    let max = 1;
    for (const key of Object.keys(counts)) {
      if (counts[key] > max) max = counts[key];
    }

    return max;
  },
);

export const getTeamSkillMatrixHierarchy = createSelector(
  getCurrentTeamSkills,
  getTeamMatrixSkillsToDisplay,
  getSelectedSkillProficiencyRange,
  (teamSkills, skillsToDisplay, skillProficiencyRange) => {
    const minIndex = getSkillRatingIndex(skillProficiencyRange.min);
    const maxRaw = skillProficiencyRange.max;
    const maxIndex =
      maxRaw > 5 && maxRaw !== 99 ? 5 : getSkillRatingIndex(maxRaw);

    const filteredTeamSkills = (teamSkills ?? []).filter(
      (skill) =>
        skillsToDisplay.includes(skill.Name) &&
        getSkillRatingIndex(skill.SkillRating) >= minIndex &&
        getSkillRatingIndex(skill.SkillRating) <= maxIndex,
    );

    const buildNodeFromSkill = (
      skill: ISkillNodeWithRating,
    ): ITeamSkillNode => ({
      skill: skill.Name,
      skillData: skill,
      ratings: new Map<SkillRating, number>(),
      children: [],
    });

    const skills = new Map<string, ITeamSkillNode>();

    filteredTeamSkills.forEach((skill) => {
      if (!skills.get(skill.Name)) {
        skills.set(skill.Name, buildNodeFromSkill(skill));
      }

      const node = skills.get(skill.Name)!;
      const ratings = node.ratings ?? new Map<SkillRating, number>();
      ratings.set(skill.SkillRating, (ratings.get(skill.SkillRating) ?? 0) + 1);
      node.ratings = ratings;
    });

    return Array.from(skills.values());
  },
);

function updateTeams(newTeam: ITeam, teams: ITeam[]): ITeam[] {
  const index = teams.findIndex((t) => t.Id === newTeam.Id);

  if (index !== -1) {
    teams.splice(index, 1, newTeam);
  } else {
    teams.push(newTeam);
  }

  return teams;
}
